import { z } from 'zod';
import { orderCoreSchema, optionalNumber, optionalString } from '../../OrderForm/orderCore.schema';
import type { CarrierCompany } from './types';

/** Checa só que é objeto: descrever os campos recusaria o save de um registro com campo nulo. */
const isObject = (val: unknown) => typeof val === 'object' && val !== null;

const carrierSchema = z.custom<CarrierCompany>(isObject);

const deadlineItemSchema = z.preprocess((val) => {
  if (val && typeof val === 'object' && 'id' in val && 'label' in val) {
    const item = val as any;
    return {
      id: String(item.id || ''),
      label: String(item.label || ''),
      checked: Boolean(item.checked),
      value: item.value ? String(item.value) : undefined,
    };
  }

  return { id: '', label: '', checked: false, value: undefined };
}, z.object({
  id: z.string(),
  label: z.string(),
  checked: z.boolean(),
  value: optionalString,
}));

const deadlinesSchema = z.preprocess((val) => {
  if (val && typeof val === 'object' && 'internal' in val && 'external' in val) {
    const deadlines = val as any;
    return {
      internal: Array.isArray(deadlines.internal) ? deadlines.internal : [],
      external: Array.isArray(deadlines.external) ? deadlines.external : [],
    };
  }

  return { internal: [], external: [] };
}, z.object({
  internal: z.array(deadlineItemSchema),
  external: z.array(deadlineItemSchema),
}));

const heatTreatmentSchema = z.preprocess((val) => {
  if (val && typeof val === 'object') {
    const treatment = val as any;
    return {
      tensionRelief: Boolean(treatment.tensionRelief),
      performance: Boolean(treatment.performance),
      aging: Boolean(treatment.aging),
      normalizationAndTempering: Boolean(treatment.normalizationAndTempering),
      annealing: Boolean(treatment.annealing),
      solutionTreatment: Boolean(treatment.solutionTreatment),
      quenchingAndTempering: Boolean(treatment.quenchingAndTempering),
    };
  }

  return {
    tensionRelief: false,
    performance: false,
    aging: false,
    normalizationAndTempering: false,
    annealing: false,
    solutionTreatment: false,
    quenchingAndTempering: false,
  };
}, z.object({
  tensionRelief: z.boolean(),
  performance: z.boolean(),
  aging: z.boolean(),
  normalizationAndTempering: z.boolean(),
  annealing: z.boolean(),
  solutionTreatment: z.boolean(),
  quenchingAndTempering: z.boolean(),
}));

const itemTaxSchema = z.object({
  taxScenarioId: optionalNumber,
  aliquotaICMS: optionalNumber,
  aliquotaIPI: optionalNumber,
  aliquotaIBS: optionalNumber,
  aliquotaCBS: optionalNumber,
  aliquotaPIS: optionalNumber,
  aliquotaCOFINS: optionalNumber,
  aliquotaICMSResolvida: optionalNumber,
  aliquotaIPIResolvida: optionalNumber,
  aliquotaIBSResolvida: optionalNumber,
  aliquotaCBSResolvida: optionalNumber,
  resolvidoEm: optionalString,
  // Preenchido pela cotação quando o item não pertence à natureza escolhida.
  motivoNaoResolvido: optionalString,
  // Origem da resolução de IPI (D4): 'SEM_REGRA' barra a emissão — ver aviso na seção.
  origemIpi: optionalString,
});

const priceDetailsSchema = z.object({
  pricePerKgMin: optionalNumber,
  pricePerKgFinal: optionalNumber,
  unitPrice: optionalNumber,
  itemSubtotal: optionalNumber,
  itemTaxes: optionalNumber,

  serviceUnit: optionalString,
  discount: z.preprocess((val) => {
    if (val === '' || val === null || val === undefined || val === 'null') return undefined;
    const num = Number(String(val).replace(',', '.'));
    return isNaN(num) ? undefined : num;
  }, z.number().min(0, "Desconto não pode ser negativo").optional()),
  sellingPrice: optionalNumber,
});

const requestedProductMaterialSchema = z.object({
  product: optionalString,
  format: optionalString,
  diameter: optionalNumber,
  thickness: optionalNumber,
  length: optionalNumber,
  finishing: optionalString,
  weigth: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === '' || val === 'null') {
        return undefined;
      }
      if (typeof val === 'string') {
        const trimmedVal = val.trim().toLowerCase();
        if (trimmedVal === 'auto') return 'Auto';
        if (trimmedVal === '') return undefined;
        const num = Number(val);
        return isNaN(num) ? undefined : num;
      }
      if (typeof val === 'number' && !isNaN(val)) {
        return val;
      }
      return undefined;
    },
    z.union([z.literal('Auto'), z.number()]).optional()
  ),
  quantity: optionalNumber,
  quantityPieces: optionalNumber,
  ncm: optionalString,
  specs: optionalString,
});

const offeredProductMaterialSchema = z.object({
  product: optionalString,
  format: optionalString,
  diameter: optionalNumber,
  thickness: optionalNumber,
  length: optionalNumber,
  finishing: optionalString,
  weigth: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === '' || val === 'null') {
        return undefined;
      }
      if (typeof val === 'string') {
        const trimmedVal = val.trim().toLowerCase();
        if (trimmedVal === 'auto') return 'Auto';
        if (trimmedVal === '') return undefined;
        const num = Number(val);
        return isNaN(num) ? undefined : num;
      }
      if (typeof val === 'number' && !isNaN(val)) {
        return val;
      }
      return undefined;
    },
    z.union([z.literal('Auto'), z.number()]).optional()
  ),
  quantity: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === '' || val === 'null') {
        return undefined;
      }
      if (typeof val === 'number' && !isNaN(val)) {
        return val;
      }
      if (typeof val === 'string') {
        const num = Number(val.trim());
        return isNaN(num) ? undefined : num;
      }
      return undefined;
    },
    z.number().optional()
  ),
  ncm: optionalString,
  specs: optionalString,
});

const finalProductSchema = z.object({
  produto: optionalString,
  codigo: optionalString,
  nomeCustomizado: optionalString,
  fabricante: optionalString,
  script: optionalString,
  quantidade: optionalNumber,
  quantidadePecas: optionalNumber,
  anexos: z.array(z.string()).optional(),
  observacoes: optionalString,
});

const resourceSchema = z.object({
  insumo: optionalString,
  categoria: optionalString,
  tipo: optionalString,
  fornecedor: optionalString,
  quantidade: optionalNumber,
  quantidadePecas: optionalNumber,
  anexos: z.array(z.string()).optional(),
  observacoes: optionalString,
});

const stageSchema = z.object({
  servico: optionalString,
  categoria: optionalString,
  tipo: optionalString,
  fornecedor: optionalString,
  quantidade: optionalNumber,
  quantidadePecas: optionalNumber,
  anexos: z.array(z.string()).optional(),
  observacoes: optionalString,
});

const feedstockSchema = z.object({
  materiaPrima: optionalString,
  quantidade: optionalNumber,
  quantidadePecas: optionalNumber,
  anexos: z.array(z.string()).optional(),
  observacoes: optionalString,
  stockDescription: optionalString,
  stockCostInUnit: optionalNumber,
  stockMarkup: optionalNumber,
  stockQuantities: z.array(z.object({
    quantity: optionalNumber,
    unitOfMeasurementId: optionalNumber,
    unit: optionalString,
  })).optional(),
});

export const itemOrderSchema: z.ZodType<any> = z.lazy(() => z.object({
  id: optionalNumber,
  notaEmitida: z.object({
    invoiceId: z.number(),
    serie: z.string().nullable().optional(),
    numeroNF: z.string().nullable().optional(),
  }).nullable().optional(),
  itemType: z.enum(["SERVICE", "RESOURCE", "MATERIAL", "PRODUCT"], {
    required_error: "Tipo de item é obrigatório"
  }),
  requestedProductMaterial: requestedProductMaterialSchema.optional(),
  offeredProductMaterial: offeredProductMaterialSchema.optional(),
  finalProduct: finalProductSchema.optional(),
  resource: resourceSchema.optional(),
  stage: stageSchema.optional(),
  feedstock: feedstockSchema.optional(),
  deadlines: deadlinesSchema.optional(),
  heatTreatments: heatTreatmentSchema.optional(),
  priceDetails: priceDetailsSchema.optional(),
  isSaved: z.boolean().optional().default(false),
  isPrincipal: z.boolean().optional().default(false),
  parentId: optionalNumber,
  subItems: z.array(itemOrderSchema).optional().default([]),
  isContainer: z.boolean().optional().default(false),
  materialId: optionalNumber,
  productId: optionalNumber,
  serviceId: optionalNumber,
  stageId: optionalNumber,
  resourceId: optionalNumber,
  serviceCategoryId: optionalNumber,
  serviceTypeId: optionalNumber,
  resourceCategoryId: optionalNumber,
  resourceTypeId: optionalNumber,
  serviceSupplierCompanyId: optionalNumber,
  resourceSupplierCompanyId: optionalNumber,
  scriptId: optionalNumber,
  stockId: optionalNumber,
  ncmId: optionalNumber,
  ncmCodigo: optionalString,
  tax: itemTaxSchema.optional().nullable(),
  quantity: optionalNumber,
  // sem esta chave o zodResolver poda o campo no submit
  quantityPieces: optionalNumber,
  nomeCustomizado: optionalString,
  extraInfo: optionalString,
  additionalData: optionalString,
  weight: optionalNumber,
  diameter: optionalNumber,
  thickness: optionalNumber,
  width: optionalNumber,
  length: optionalNumber,
  diameterToleranceMinus: optionalNumber,
  diameterTolerancePlus: optionalNumber,
  thicknessToleranceMinus: optionalNumber,
  thicknessTolerancePlus: optionalNumber,
  widthToleranceMinus: optionalNumber,
  widthTolerancePlus: optionalNumber,
  lengthToleranceMinus: optionalNumber,
  lengthTolerancePlus: optionalNumber,
  productionObservation: optionalString,
  // Transitórios do cálculo de massa (não persistem no backend)
  massPerPiece: optionalNumber,
  referenceMaterialId: optionalNumber,
  formatId: optionalNumber,
  finishingId: optionalNumber,
  /** rótulo do acabamento: o select com busca precisa do par {id, name}, não só do id */
  finishingName: optionalString,
  unitOfMeasurementId: optionalNumber,
  unitOfMeasurementName: optionalString,
  unitFromProduct: z.boolean().optional(),
  // Auxiliares client-side do auto-fill da Especificação (não persistem no backend)
  productNorm: optionalString,
  stockStandards: optionalString,
  itemPrice: optionalNumber,
  cost: optionalNumber,
  markup: optionalNumber,
  deliveryBusinessDays: optionalNumber,
  costUnitId: optionalNumber,
  costUnitName: optionalString,
  // Flag client-side: subitem gerado ao puxar roteiro (não persiste no backend)
  fromScript: z.boolean().optional(),
  // Espelha Stage.allowZeroCost; vem do catálogo e não persiste no OrderItem
  allowZeroCost: z.boolean().optional()
}).superRefine((data, ctx) => {
  if (data.isContainer) {
    if (!data.subItems || data.subItems.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Um container deve ter pelo menos um sub-item.",
        path: ['subItems']
      });
    }
    return;
  }

  if (data.isPrincipal || !data.parentId) {
    if (data.itemType === "MATERIAL") {
      if (!data.feedstock) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes da Matéria-Prima são obrigatórios.", path: ['feedstock'] });
      } else {
        const result = feedstockSchema.safeParse(data.feedstock);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['feedstock', ...err.path] }); });
        }
      }

      if (data.requestedProductMaterial) {
        const result = requestedProductMaterialSchema.safeParse(data.requestedProductMaterial);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['requestedProductMaterial', ...err.path] }); });
        }
      }

      if (data.offeredProductMaterial) {
        const result = offeredProductMaterialSchema.safeParse(data.offeredProductMaterial);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['offeredProductMaterial', ...err.path] }); });
        }
      }
      if (!data.deadlines) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Prazos são obrigatórios para 'Materia Prima'.", path: ['deadlines'] });
      }
      if (!data.heatTreatments) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Tratamentos Térmicos são obrigatórios para 'Materia Prima'.", path: ['heatTreatments'] });
      }
    }

    if (data.itemType === "PRODUCT") {
      if (!data.finalProduct) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Produto Final são obrigatórios.", path: ['finalProduct'] });
      } else {
        const result = finalProductSchema.safeParse(data.finalProduct);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['finalProduct', ...err.path] }); });
        }
      }
    }

    if (data.itemType === "RESOURCE") {
      if (!data.resource) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Recurso são obrigatórios.", path: ['resource'] });
      } else {
        const result = resourceSchema.safeParse(data.resource);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['resource', ...err.path] }); });
        }
      }
    }

    if (data.itemType === "SERVICE") {
      if (!data.stage) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Estágio são obrigatórios.", path: ['stage'] });
      } else {
        const result = stageSchema.safeParse(data.stage);
        if (!result.success) {
          result.error.errors.forEach(err => { ctx.addIssue({ code: z.ZodIssueCode.custom, message: err.message, path: ['stage', ...err.path] }); });
        }
      }
    }
  }

  if (data.parentId && !data.isPrincipal) {
    if (data.itemType === "PRODUCT" && !data.finalProduct) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Produto Final são obrigatórios.", path: ['finalProduct'] });
    }
    if (data.itemType === "RESOURCE" && !data.resource) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Recurso são obrigatórios.", path: ['resource'] });
    }
    if (data.itemType === "SERVICE" && !data.stage) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes do Estágio são obrigatórios.", path: ['stage'] });
    }
    if (data.itemType === "MATERIAL" && !data.feedstock) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Detalhes da Matéria-Prima são obrigatórios.", path: ['feedstock'] });
    }
  }
}));

export const installmentSchema = z.object({
  numero: z.number(),
  valor: z.number(),
  vencimento: z.string(),
  comissaoAtiva: z.boolean().optional().default(false),
  comissaoPercentual: z.number().optional(),
  diasOffset: z.number().optional(),
});

export const orderSchema = orderCoreSchema.extend({
  id: z.number().optional(),
  resource: optionalString,
  contactId: optionalNumber,
  contactName: optionalString,
  contactEmail: optionalString,
  contactPhone: optionalString,
  productionObservation: optionalString,
  deliveryBusinessDays: optionalNumber,
  // Forma de pagamento (catálogo global PaymentMethod). Campos transientes do front;
  // o submit monta `paymentMethods: [{ paymentMethodId, description? }]` (M2M).
  paymentMethodDescription: optionalString,
  shippingValue: optionalNumber,
  carrier: carrierSchema,
  fileIds: z.array(z.number()).optional(),
  attachments: z.array(z.string()).optional(),
  items: z.array(itemOrderSchema)
    .min(1, "O pedido deve conter pelo menos um item."),
  quantity: z.number().optional(),
  totalQuantity: z.number().optional(),
  taxes: optionalNumber,
  taxConfigId: optionalNumber,
  // Transiente: marca que o usuário limpou a natureza (vs. nunca ter tido uma). Só o front lê;
  // api.ts traduz para `taxConfigId: null` no PATCH e não sai no payload.
  taxConfigCleared: z.boolean().optional(),
  total: optionalNumber,
  installments: z.array(installmentSchema).optional(),
  status: z.boolean().optional().nullable(),
  closedBy: z.string().optional().nullable(),
  closedByDetails: z.record(z.any()).optional().nullable(),
}).superRefine((data, ctx) => {
  // Trava de estoque: agregada por estoque+unidade dentro do pedido; sem quantidade
  // cadastrada na unidade do item (StockQuantity), a linha nao valida.
  const usage = new Map<string, { total: number; available: number; unit: string; firstIdx: number }>();
  const collect = (it: any, idx: number) => {
    const stockId = it?.stockId;
    const unitId = it?.unitOfMeasurementId;
    const qty = Number(it?.quantity) || 0;
    if (!stockId || !unitId || qty <= 0) return;
    const sq = it?.feedstock?.stockQuantities;
    const available = Array.isArray(sq)
      ? sq.find((q: any) => Number(q?.unitOfMeasurementId) === Number(unitId))
      : undefined;
    if (!available || available.quantity == null) return;
    const key = `${stockId}:${unitId}`;
    const cur = usage.get(key);
    if (cur) {
      cur.total += qty;
    } else {
      usage.set(key, { total: qty, available: Number(available.quantity), unit: String(available.unit || ''), firstIdx: idx });
    }
  };
  (data.items || []).forEach((it: any, idx: number) => {
    collect(it, idx);
    (it?.subItems || []).forEach((sub: any) => collect(sub, idx));
  });
  usage.forEach((u) => {
    if (u.total > u.available) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Estoque insuficiente: o pedido usa ${u.total}${u.unit ? ` ${u.unit}` : ''} e o estoque tem ${u.available} disponível.`,
        path: ['items', u.firstIdx, 'quantity'],
      });
    }
  });
});

export type OrderSchemaType = z.infer<typeof orderSchema>;
export type ItemOrderSchemaType = z.infer<typeof itemOrderSchema>;
export type ProductMaterialDetailSchemaType = z.infer<typeof offeredProductMaterialSchema>;

export type ItemPath = `items.${number}` | `items.${number}.subItems.${number}`;